import html2canvas from "html2canvas";

// ─── Color helpers ────────────────────────────────────────────────────────────

/**
 * Converts a CSS rgb/rgba/hex color string to a 6-character uppercase hex
 * string suitable for pptxgenjs color options (e.g. "FF0000").
 * Returns undefined for transparent or invalid colors.
 */
export function convertCssColorToHex(cssColorString) {
  if (
    !cssColorString ||
    cssColorString === 'transparent' ||
    cssColorString === 'rgba(0, 0, 0, 0)' ||
    cssColorString === 'rgba(0,0,0,0)'
  ) {
    return undefined;
  }

  const rgbMatch = cssColorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (rgbMatch) {
    const redChannel   = parseInt(rgbMatch[1], 10);
    const greenChannel = parseInt(rgbMatch[2], 10);
    const blueChannel  = parseInt(rgbMatch[3], 10);

    if (
      isNaN(redChannel)   || isNaN(greenChannel)   || isNaN(blueChannel) ||
      redChannel < 0      || redChannel > 255 ||
      greenChannel < 0    || greenChannel > 255 ||
      blueChannel < 0     || blueChannel > 255
    ) {
      return undefined;
    }

    return (
      redChannel  .toString(16).padStart(2, '0').toUpperCase() +
      greenChannel.toString(16).padStart(2, '0').toUpperCase() +
      blueChannel .toString(16).padStart(2, '0').toUpperCase()
    );
  }

  if (cssColorString.startsWith('#')) {
    const hexValue = cssColorString.substring(1).toUpperCase();
    if (/^[0-9A-F]{6}$/.test(hexValue)) return hexValue;
  }

  return undefined;
}

// ─── Unit conversion ──────────────────────────────────────────────────────────

export const cmToInches = (cm) => cm / 2.54;

// ─── Constants ────────────────────────────────────────────────────────────────

// Default LAYOUT_16x9: 10" x 5.625" = 25.4cm x 14.29cm; 0.5cm margins → 24.4cm usable width
export const SLIDE_TITLE_TEXT_OPTIONS = {
  x: cmToInches(0.5),
  y: cmToInches(0.8),
  w: cmToInches(24.4),
  h: cmToInches(1.5),
  fontSize: 18,
  fontFace: 'Montserrat',
  bold: true,
  align: 'left',
  valign: 'middle',
};

// Azure Blob Storage base URL used for the cover slide image only
export const BLOB_STORAGE_BASE_URL = 'https://skygeniwebapp.blob.core.windows.net/pptx-slides';

// Position map for Image assets at each Location.
// fixed: true  → use w/h exactly (background/pattern images)
// fixed: false → w/h are max bounds; actual size preserves aspect ratio
export const MASTER_IMAGE_POSITION_MAP = {
  Top_Left:    { x: cmToInches(0.4),  y: cmToInches(0.3),  w: cmToInches(16.0), h: cmToInches(0.5), fixed: false },
  Top_Right:   { x: null,           y: 0,                 w: cmToInches(4.0),  h: cmToInches(2.5), fixed: false },
  Bottom_Left: { x: 0,                y: cmToInches(9.29),  w: cmToInches(5.5),  h: cmToInches(5.0), fixed: true  },
  Bottom_Right:{ x: cmToInches(20.4), y: cmToInches(9.29),  w: cmToInches(5.0),  h: cmToInches(5.0), fixed: true  },
};

// Position map for Text assets at each Location.
// Bottom_Left text is footer-level (left-aligned), not the image corner zone.
export const MASTER_TEXT_POSITION_MAP = {
  Top_Left:    { x: cmToInches(0.4),  y: cmToInches(0.3),   w: cmToInches(16.0), h: cmToInches(0.5),  align: 'left'  },
  Top_Right:   { x: cmToInches(19.4), y: 0,                  w: cmToInches(6.0),  h: cmToInches(5.0),  align: 'right' },
  Bottom_Left: { x: cmToInches(0.5),  y: cmToInches(13.39), w: cmToInches(9.5),  h: cmToInches(0.6),  align: 'left'  },
  Bottom_Right:{ x: cmToInches(10.0), y: cmToInches(13.39), w: cmToInches(14.5), h: cmToInches(0.6),  align: 'right' },
};

// ─── Image loading ────────────────────────────────────────────────────────────

/**
 * Loads an image from a URL and returns its base64 data URL together with
 * its natural pixel dimensions. Uses an offscreen canvas for the conversion.
 */
export function fetchImageAsBase64DataUrl(imagePath) {
  return new Promise((resolve, reject) => {
    const imageElement = new Image();
    imageElement.crossOrigin = 'anonymous';

    imageElement.onload = () => {
      try {
        const offscreenCanvas = document.createElement('canvas');
        offscreenCanvas.width  = imageElement.width;
        offscreenCanvas.height = imageElement.height;
        const canvasRenderingContext = offscreenCanvas.getContext('2d');
        canvasRenderingContext.drawImage(imageElement, 0, 0);
        const base64DataUrl = offscreenCanvas.toDataURL('image/png');
        if (!base64DataUrl || base64DataUrl === 'data:,') {
          throw new Error('Failed to convert image to base64');
        }
        resolve({ dataUrl: base64DataUrl, width: imageElement.width, height: imageElement.height });
      } catch (error) {
        console.error(`Error converting image ${imagePath} to base64:`, error);
        reject(error);
      }
    };

    imageElement.onerror = (error) => {
      console.error(`Error loading image ${imagePath}:`, error);
      reject(new Error(`Failed to load image: ${imagePath}`));
    };

    imageElement.src = imagePath;
  });
}

// ─── Master slide builder ─────────────────────────────────────────────────────

/**
 * Parses SLIDE_MASTER_CONFIG rows fetched from the backend into pptxgenjs
 * master slide objects, cover slide text entries, and location metadata.
 *
 * Returns:
 *   slideMasterObjectsList  – pptxgenjs master objects array (images + footer text)
 *   pageNumberConfig        – pptxgenjs slideNumber options object (or null if not configured)
 *   locationToImageDataMap  – map of Location → { dataUrl, width, height }
 *   coverSlideTextList      – footer text entries to render on the cover slide
 *   locationToAssetTypeMap  – map of Location → 'Image' | 'Text'
 */
export async function parseSlideMasterConfigToObjects(config, company = '') {
  const slideMasterObjectsList = [];
  const locationToImageDataMap = {};
  const coverSlideTextList     = [];
  const locationToAssetTypeMap = {};
  let pageNumberConfig = null;

  for (const row of config) {
    const assetTypes  = row.Asset_Type.split(';');
    const assetValues = row.Asset_Value.split(';');
    let pageNumberFoundInRow = false;

    for (let i = 0; i < assetTypes.length; i++) {
      const assetType  = assetTypes[i].trim();
      const assetValue = (assetValues[i] || '').trim();

      if (assetType === 'Image') {
        try {
          const imageData = await fetchImageAsBase64DataUrl(assetValue);
          locationToImageDataMap[row.Location] = {
            dataUrl: imageData.dataUrl,
            width:   imageData.width,
            height:  imageData.height,
          };
          locationToAssetTypeMap[row.Location] = 'Image';

          if (row.Location === 'Bottom_Middle') {
            const slideWidthCm          = 25.4;
            const desiredHeightCm       = 0.35;
            const bottomMiddleMaxWidthCm = 3.0;
            const imageAspectRatio      = imageData.width / imageData.height;
            let clampedHeightCm = desiredHeightCm;
            let clampedWidthCm  = desiredHeightCm * imageAspectRatio;
            if (clampedWidthCm > bottomMiddleMaxWidthCm) {
              clampedWidthCm  = bottomMiddleMaxWidthCm;
              clampedHeightCm = bottomMiddleMaxWidthCm / imageAspectRatio;
            }
            const centerXPosCm = (slideWidthCm - clampedWidthCm) / 2;
            slideMasterObjectsList.push({
              image: {
                data: imageData.dataUrl,
                x: cmToInches(centerXPosCm),
                y: cmToInches(13.39),
                w: cmToInches(clampedWidthCm),
                h: cmToInches(clampedHeightCm),
              },
            });
          } else if (row.Location === 'Top_Right') {
            const topRightAspect = imageData.width / imageData.height;
            const topRightMaxW = company === 'C0013' ? 8.0 : 4.0;
            const topRightMaxH = company === 'C0013' ? 4.5 : 2.5;
            let topRightW = topRightMaxW, topRightH = topRightMaxW / topRightAspect;
            if (topRightH > topRightMaxH) { topRightH = topRightMaxH; topRightW = topRightMaxH * topRightAspect; }
            slideMasterObjectsList.push({
              image: {
                data: imageData.dataUrl,
                x: cmToInches(25.4 - topRightW),
                y: 0,
                w: cmToInches(topRightW),
                h: cmToInches(topRightH),
              },
            });
          } else if (row.Location === 'Bottom_Right') {
            const slideWidthCm           = 25.4;
            const bottomRightMaxWidthCm  = 2.5;
            const bottomRightMaxHeightCm = 0.9;
            const bottomRightImageAspect = imageData.width / imageData.height;
            const bottomRightBoxAspect   = bottomRightMaxWidthCm / bottomRightMaxHeightCm;
            let bottomRightFinalWidthCm, bottomRightFinalHeightCm;
            if (bottomRightImageAspect > bottomRightBoxAspect) {
              bottomRightFinalWidthCm  = bottomRightMaxWidthCm;
              bottomRightFinalHeightCm = bottomRightMaxWidthCm / bottomRightImageAspect;
            } else {
              bottomRightFinalHeightCm = bottomRightMaxHeightCm;
              bottomRightFinalWidthCm  = bottomRightMaxHeightCm * bottomRightImageAspect;
            }
            slideMasterObjectsList.push({
              image: {
                data: imageData.dataUrl,
                x: cmToInches(slideWidthCm - bottomRightFinalWidthCm),
                y: cmToInches(13.30),
                w: cmToInches(bottomRightFinalWidthCm),
                h: cmToInches(bottomRightFinalHeightCm),
              },
            });
          } else {
            const locationPos = MASTER_IMAGE_POSITION_MAP[row.Location];
            if (!locationPos) continue;

            let masterImageWidthInches, masterImageHeightInches;
            if (locationPos.fixed) {
              masterImageWidthInches  = locationPos.w;
              masterImageHeightInches = locationPos.h;
            } else {
              const masterImageAspect = imageData.width / imageData.height;
              masterImageWidthInches  = locationPos.w;
              masterImageHeightInches = locationPos.w / masterImageAspect;
              if (masterImageHeightInches > locationPos.h) {
                masterImageHeightInches = locationPos.h;
                masterImageWidthInches  = locationPos.h * masterImageAspect;
              }
            }
            slideMasterObjectsList.push({
              image: {
                data: imageData.dataUrl,
                x: locationPos.x,
                y: locationPos.y,
                w: masterImageWidthInches,
                h: masterImageHeightInches,
              },
            });
          }
        } catch (e) {
          console.error(`Failed to load master slide image for ${row.Location}:`, e);
        }
      } else if (assetType === 'Text') {
        const locationPos = MASTER_TEXT_POSITION_MAP[row.Location];
        if (!locationPos) continue;
        locationToAssetTypeMap[row.Location] = 'Text';

        if (assetValue === '<Page_Number>') {
          const isPageNumberOnLeft = row.Location === 'Bottom_Left';
          pageNumberConfig = {
            x: isPageNumberOnLeft ? cmToInches(0.5) : cmToInches(24.0),
            y: cmToInches(13.39),
            w: cmToInches(1.0),
            h: cmToInches(0.6),
            color: '7F7F80',
            fontSize: 6,
            align: isPageNumberOnLeft ? 'left' : 'right',
            valign: 'bottom',
          };
          pageNumberFoundInRow = true;

          // Handle companion copyright text when Asset_Value has more parts than Asset_Type
          // e.g. Asset_Type="Text", Asset_Value="<Page_Number>;© <YYYY> PiedPiper"
          const extraValueStartIndex = assetTypes.length; // values beyond the types array
          for (let j = extraValueStartIndex; j < assetValues.length; j++) {
            const rawCompanionValue = assetValues[j].trim();
            if (!rawCompanionValue) continue;
            const processedCompanionText = rawCompanionValue.replace('<YYYY>', String(new Date().getFullYear()));
            const companionXPosInches  = isPageNumberOnLeft ? cmToInches(1.0) : cmToInches(18.5);
            const companionWidthInches = isPageNumberOnLeft ? cmToInches(7.5) : cmToInches(5.5);
            const companionTextAlign   = isPageNumberOnLeft ? 'left' : 'right';
            slideMasterObjectsList.push({
              text: {
                text: processedCompanionText,
                options: {
                  x: companionXPosInches,
                  y: cmToInches(13.39),
                  w: companionWidthInches,
                  h: cmToInches(0.6),
                  fontSize: 6,
                  color: '7F7F80',
                  align: companionTextAlign,
                  valign: 'bottom',
                },
              },
            });
            coverSlideTextList.push({
              text: processedCompanionText,
              x: companionXPosInches,
              y: cmToInches(13.39),
              w: companionWidthInches,
              h: cmToInches(0.6),
              align: companionTextAlign,
            });
          }
        } else {
          const processedTextValue = assetValue.replace('<YYYY>', String(new Date().getFullYear()));

          // When page number is set in the same row, offset text to avoid overlap
          let textXInches     = locationPos.x;
          let textWidthInches = locationPos.w;
          if (pageNumberFoundInRow) {
            if (row.Location === 'Bottom_Left')  { textXInches = cmToInches(2.0);  textWidthInches = cmToInches(7.5); }
            if (row.Location === 'Bottom_Right') { textXInches = cmToInches(18.5); textWidthInches = cmToInches(5.5); }
          }

          slideMasterObjectsList.push({
            text: {
              text: processedTextValue,
              options: {
                x: textXInches, y: locationPos.y, w: textWidthInches, h: locationPos.h,
                fontSize: 6,
                color: '7F7F80',
                align: locationPos.align || 'right',
                valign: 'bottom',
              },
            },
          });

          coverSlideTextList.push({
            text: processedTextValue,
            x: textXInches, y: locationPos.y, w: textWidthInches, h: locationPos.h,
            align: locationPos.align || 'right',
          });
        }
      }
    }
  }

  return {
    slideMasterObjectsList,
    pageNumberConfig,
    locationToImageDataMap,
    coverSlideTextList,
    locationToAssetTypeMap,
  };
}

// ─── DOM helpers ──────────────────────────────────────────────────────────────

/** Returns true if the element contains at least one currently visible <table>. */
export function hasVisibleTable(element) {
  const table = element.querySelector('table');
  return table !== null && table.offsetParent !== null;
}

/**
 * Reads the slide title from a MUI Paper DOM element by looking for the
 * first MuiTypography-h5/h6 element, then falls back to any Typography
 * text that looks like a reasonable title (10–150 chars).
 */
export function extractTitleFromPaperElement(paperElement) {
  const h5TitleElement = paperElement.querySelector('[class*="MuiTypography-h5"]');
  if (h5TitleElement) return h5TitleElement.textContent.trim();

  const h6TitleElement = paperElement.querySelector('[class*="MuiTypography-h6"]');
  if (h6TitleElement) return h6TitleElement.textContent.trim();

  const typographyElements = paperElement.querySelectorAll('[class*="MuiTypography-"]');
  for (const el of typographyElements) {
    const text = el.textContent.trim();
    if (text && text.length > 10 && text.length < 150) return text;
  }

  return null;
}

/**
 * Walks a DOM <table> element and returns its content as a 2-D array of
 * pptxgenjs cell objects, preserving background colors, text colors, bold,
 * italic, alignment, and colspan values.
 */
export function convertDomTableToPptxRows(tableElement) {
  if (!tableElement) return null;

  const domTableRows = Array.from(tableElement.querySelectorAll('tr'));
  const pptxRows     = [];

  domTableRows.forEach((tableRow) => {
    const rowCells           = Array.from(tableRow.querySelectorAll('th, td'));
    const pptxRowCells       = [];
    const rowBackgroundColor = window.getComputedStyle(tableRow).backgroundColor;

    rowCells.forEach((cell) => {
      const cellTextContent = cell.textContent.trim().replace(/\s+/g, ' ');
      const cellColSpan = parseInt(
        cell.getAttribute('colspan') || cell.getAttribute('colSpan') || cell.colSpan || '1'
      ) || 1;

      const cellComputedStyle  = window.getComputedStyle(cell);
      const cellBackgroundColor = cellComputedStyle.backgroundColor;
      const cellTextColor       = cellComputedStyle.color;
      const cellFontWeight      = cellComputedStyle.fontWeight;
      const cellFontStyle       = cellComputedStyle.fontStyle;
      const cellTextAlign       = cellComputedStyle.textAlign || 'left';
      const cellVerticalAlign   = cellComputedStyle.verticalAlign || 'middle';

      const pptxCell        = { text: String(cellTextContent || '').replace(/\0/g, '') };
      const pptxCellOptions = {};

      // Background — fall back to row color if cell is transparent
      let resolvedBackgroundColor = cellBackgroundColor;
      if (
        (!cellBackgroundColor || cellBackgroundColor === 'rgba(0, 0, 0, 0)' || cellBackgroundColor === 'transparent') &&
        rowBackgroundColor && rowBackgroundColor !== 'rgba(0, 0, 0, 0)' && rowBackgroundColor !== 'transparent'
      ) {
        resolvedBackgroundColor = rowBackgroundColor;
      }
      const backgroundHexColor = convertCssColorToHex(resolvedBackgroundColor);
      if (backgroundHexColor) pptxCellOptions.fill = backgroundHexColor;

      const textHexColor = convertCssColorToHex(cellTextColor);
      if (textHexColor) pptxCellOptions.color = textHexColor;

      const cellHasBoldFont = cellFontWeight === 'bold' || parseInt(cellFontWeight) >= 600;
      if (cellHasBoldFont) pptxCellOptions.bold = true;

      if (cellFontStyle === 'italic') pptxCellOptions.italic = true;

      pptxCellOptions.fontSize = 6;
      pptxCellOptions.fontFace = 'Roboto';

      if (cellTextAlign === 'center')     pptxCellOptions.align = 'center';
      else if (cellTextAlign === 'right') pptxCellOptions.align = 'right';
      else                                pptxCellOptions.align = 'left';

      if (cellVerticalAlign === 'top')         pptxCellOptions.valign = 'top';
      else if (cellVerticalAlign === 'bottom') pptxCellOptions.valign = 'bottom';
      else                                     pptxCellOptions.valign = 'middle';

      if (cellColSpan > 1 && cellColSpan <= 100) pptxCellOptions.colspan = cellColSpan;

      pptxCell.options = pptxCellOptions;
      pptxRowCells.push(pptxCell);
    });

    if (pptxRowCells.length > 0) pptxRows.push(pptxRowCells);
  });

  return pptxRows;
}

// ─── PPTX slide builders ──────────────────────────────────────────────────────

/**
 * Converts all visible DOM <table> elements inside a Paper element into
 * pptxgenjs table slides. The first table uses `firstSlide`; additional
 * tables or paginated chunks each get a new slide added automatically.
 */
export function renderDomTableAsSlides(pptx, firstSlide, element, slideTitle) {
  try {
    const allTableElements     = Array.from(element.querySelectorAll('table'));
    const visibleTableElements = allTableElements.filter(table => table.offsetParent !== null);

    if (visibleTableElements.length === 0) {
      console.warn('No visible tables found in element');
      return;
    }

    let firstSlideConsumed = false;

    for (const tableElement of visibleTableElements) {
      const pptxTableRows = convertDomTableToPptxRows(tableElement);
      if (!pptxTableRows || pptxTableRows.length === 0) continue;

      // Resolve table-specific title from a preceding Typography sibling
      let resolvedSlideTitle      = slideTitle;
      const tableContainerElement = tableElement.closest('[class*="MuiBox-root"]') || tableElement.parentElement;
      if (tableContainerElement) {
        const precedingElement = tableContainerElement.previousElementSibling;
        if (precedingElement) {
          const siblingTitleElement =
            precedingElement.querySelector('[class*="MuiTypography-"]') ||
            (precedingElement.classList.contains('MuiTypography-root') ? precedingElement : null);
          if (siblingTitleElement) {
            const siblingTitleText = siblingTitleElement.textContent.trim();
            if (siblingTitleText && siblingTitleText.length > 2 && siblingTitleText.length < 100) {
              resolvedSlideTitle = siblingTitleText;
            }
          }
        }
      }

      const domTableRows    = Array.from(tableElement.querySelectorAll('tr'));
      const tableComputedCss = window.getComputedStyle(tableElement);

      // Border
      let tableBorderHexColor = null;
      let tableBorderWidthPt  = 1;
      const rawBorderColor = tableComputedCss.borderColor || tableComputedCss.borderTopColor;
      if (rawBorderColor && rawBorderColor !== 'rgba(0, 0, 0, 0)' && rawBorderColor !== 'transparent') {
        tableBorderHexColor = convertCssColorToHex(rawBorderColor);
      }
      const rawBorderWidthPx = parseFloat(tableComputedCss.borderWidth || tableComputedCss.borderTopWidth);
      if (rawBorderWidthPx && !isNaN(rawBorderWidthPx) && rawBorderWidthPx > 0) {
        tableBorderWidthPt = Math.max(1, Math.round(rawBorderWidthPx));
      }

      // Column widths from first row (in DOM pixels)
      const domColumnWidthsPx = [];
      let totalDomWidthPx     = 0;
      const firstTableRow = domTableRows[0];
      if (firstTableRow) {
        Array.from(firstTableRow.querySelectorAll('th, td')).forEach((cell) => {
          const cellWidthPx   = parseFloat(window.getComputedStyle(cell).width) || cell.offsetWidth;
          const cellColSpan   = parseInt(cell.getAttribute('colspan') || '1');
          const perColWidthPx = cellWidthPx / cellColSpan;
          for (let i = 0; i < cellColSpan; i++) {
            domColumnWidthsPx.push(perColWidthPx);
            totalDomWidthPx += perColWidthPx;
          }
        });
      }

      const maxColumnCount = Math.max(...pptxTableRows.map(row => {
        let cols = 0;
        row.forEach(cell => { cols += (cell.options?.colspan || 1); });
        return cols;
      }));

      // Scale column widths to fit slide (usable width = 24.4cm)
      const usableWidthCm = 24.4;
      let pptxColumnWidthsInches;
      if (domColumnWidthsPx.length === maxColumnCount && totalDomWidthPx > 0) {
        const domToCmScaleFactor = usableWidthCm / (totalDomWidthPx / 96 * 2.54);
        pptxColumnWidthsInches = domColumnWidthsPx.map(
          widthPx => cmToInches((widthPx / 96 * 2.54) * domToCmScaleFactor)
        );
      } else {
        pptxColumnWidthsInches = Array(maxColumnCount).fill(cmToInches(usableWidthCm / maxColumnCount));
      }

      // Detect header rows (th elements or bold-styled rows)
      const detectedHeaderRows = [];
      let headerRowsCount = 0;
      for (let i = 0; i < Math.min(2, domTableRows.length); i++) {
        const hasTh  = domTableRows[i].querySelector('th') !== null;
        const isBold = pptxTableRows[i] && pptxTableRows[i].some(cell => cell.options?.bold);
        if (hasTh || isBold) {
          detectedHeaderRows.push(pptxTableRows[i]);
          headerRowsCount++;
        } else {
          break;
        }
      }

      const bodyRows = pptxTableRows.slice(headerRowsCount);

      // Rows-per-slide calculation
      // Table area: y 2.4cm – 12.8cm (leaves room for footer at ~13.0cm)
      const baseDataRowHeightCm   = 0.6;
      const columnCountMultiplier = maxColumnCount > 10 ? 1.3 : maxColumnCount > 6 ? 1.2 : 1.1;
      const scaledDataRowHeightCm  = baseDataRowHeightCm * columnCountMultiplier;
      const scaledHeaderRowHeightCm = 0.85 * columnCountMultiplier;
      const tableAreaTopCm     = 2.4;
      const tableAreaBottomCm  = 12.8;
      const tableAreaHeightCm  = tableAreaBottomCm - tableAreaTopCm;
      const headerAreaHeightCm = detectedHeaderRows.length * scaledHeaderRowHeightCm;
      const bodyAreaHeightCm   = tableAreaHeightCm - headerAreaHeightCm;
      const maxBodyRowsPerSlide = Math.max(1, Math.floor((bodyAreaHeightCm * 0.85) / scaledDataRowHeightCm));

      // Split body rows into page-sized chunks
      const bodyRowChunks = [];
      if (bodyRows.length > 0) {
        for (let i = 0; i < bodyRows.length; i += maxBodyRowsPerSlide) {
          bodyRowChunks.push(bodyRows.slice(i, i + maxBodyRowsPerSlide));
        }
      } else if (detectedHeaderRows.length > 0) {
        bodyRowChunks.push([]);
      }

      bodyRowChunks.forEach((chunk, chunkIndex) => {
        let slide;
        if (chunkIndex === 0 && !firstSlideConsumed) {
          slide = firstSlide;
          firstSlideConsumed = true;
        } else {
          slide = pptx.addSlide({ masterName: 'COLORTOKENS_MASTER' });
        }

        const chunkSlideTitle =
          bodyRowChunks.length > 1 && chunkIndex > 0
            ? `${resolvedSlideTitle} (Page ${chunkIndex + 1} of ${bodyRowChunks.length})`
            : resolvedSlideTitle;

        slide.addText(chunkSlideTitle, SLIDE_TITLE_TEXT_OPTIONS);

        const combinedSlideRows = [...detectedHeaderRows];
        const styledBodyRows = chunk.map(row =>
          row.map(cell => ({
            ...cell,
            options: { ...cell.options, fontSize: 6, fontFace: 'Roboto' },
          }))
        );
        combinedSlideRows.push(...styledBodyRows);

        if (combinedSlideRows.length > 0) {
          const totalTableWidthInches = pptxColumnWidthsInches.reduce((sum, w) => sum + w, 0);
          const validatedColWidthsInches = pptxColumnWidthsInches.map(w =>
            typeof w !== 'number' || isNaN(w) || !isFinite(w) || w <= 0
              ? cmToInches(1.0)
              : Math.max(0.1, w)
          );

          const pptxTableOptions = {
            x: cmToInches(0.5),
            y: cmToInches(2.4),
            w: Math.max(0.1, totalTableWidthInches),
            colW: validatedColWidthsInches,
            fontFace: 'Roboto',
            fontSize: 6,
          };

          if (tableBorderHexColor && /^[0-9A-Fa-f]{6}$/i.test(tableBorderHexColor)) {
            pptxTableOptions.border = {
              type: "solid",
              color: tableBorderHexColor.toUpperCase(),
              pt: Math.max(1, Math.min(10, tableBorderWidthPt)),
            };
          } else {
            pptxTableOptions.border = { type: "solid", color: "CCCCCC", pt: 1 };
          }

          try {
            slide.addTable(combinedSlideRows, pptxTableOptions);
          } catch (error) {
            console.error('Error adding table:', error);
          }
        }
      });
    }
  } catch (error) {
    console.error('Error in renderDomTableAsSlides:', error);
  }
}

/**
 * Screenshots a chart DOM element using html2canvas and adds it to a
 * pptxgenjs slide as a centred image. Chart heading elements are hidden
 * during capture to avoid rendering them twice (the title is added
 * separately via addText).
 */
export async function captureChartElementToSlide(slide, element, slideTitle) {
  try {
    // Temporarily hide h5/h6 Typography titles before capture so they don't appear twice
    const chartHeadingElements = [];
    [
      ...element.querySelectorAll('[class*="MuiTypography-h5"]'),
      ...element.querySelectorAll('[class*="MuiTypography-h6"]'),
    ].forEach(el => {
      chartHeadingElements.push({ element: el, originalDisplay: el.style.display });
      el.style.display = 'none';
    });

    const capturedCanvas = await html2canvas(element, {
      backgroundColor: "#FFFFFF",
      scale: 2,
      logging: false,
      useCORS: true,
    });

    chartHeadingElements.forEach(({ element: el, originalDisplay }) => {
      el.style.display = originalDisplay || '';
    });

    const capturedImageBase64 = capturedCanvas.toDataURL();
    const capturedWidthPx     = capturedCanvas.width;
    const capturedHeightPx    = capturedCanvas.height;

    slide.addText(slideTitle, SLIDE_TITLE_TEXT_OPTIONS);

    // Scale image to fit within the usable slide area (centred horizontally)
    const slideWidthCm      = 25.4;
    const maxChartWidthCm   = 24.4;
    const maxChartHeightCm  = 10.5;
    const chartStartYCm     = 2.4;
    const capturedAspectRatio = capturedWidthPx / capturedHeightPx;
    const slideMaxAspectRatio = maxChartWidthCm / maxChartHeightCm;

    let scaledChartWidthCm, scaledChartHeightCm;
    if (capturedAspectRatio > slideMaxAspectRatio) {
      scaledChartWidthCm  = maxChartWidthCm;
      scaledChartHeightCm = scaledChartWidthCm / capturedAspectRatio;
    } else {
      scaledChartHeightCm = maxChartHeightCm;
      scaledChartWidthCm  = scaledChartHeightCm * capturedAspectRatio;
    }

    const centeredXPosCm = (slideWidthCm - scaledChartWidthCm) / 2;

    slide.addImage({
      data: capturedImageBase64,
      x: cmToInches(centeredXPosCm),
      y: cmToInches(chartStartYCm),
      w: cmToInches(scaledChartWidthCm),
      h: cmToInches(scaledChartHeightCm),
    });
  } catch (error) {
    console.error('Error adding chart image:', error);
  }
}
